import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { SubscriptionAccessService } from 'src/app/shared/services/subscription-access.service';

export interface RenewDialogData {
  subscription: any;
}

@Component({
  selector: 'app-renew-dialog',
  templateUrl: './renew-dialog.component.html',
  styleUrls: ['./renew-dialog.component.css'],
})
export class RenewDialogComponent implements OnInit {
  loading = false;
  calculating = false;
  duration: number;
  monthOptions: number[] = Array.from({ length: 22 }, (_, i) => i + 3);
  subtotal = 0;
  gstAmount = 0;
  orderTotal = 0;

  constructor(
    private apiService: ApiService,
    private toastrService: ToastrMsgService,
    private subscriptionAccessService: SubscriptionAccessService,
    public dialogRef: MatDialogRef<RenewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RenewDialogData
  ) {
    this.duration = Number(data.subscription?.duration) || 3;
    if (this.duration < 3) this.duration = 3;
  }

  ngOnInit(): void {
    this.recalculate();
  }

  private domainPayload(): Record<string, { users_count: number }> {
    const sub = this.data.subscription || {};
    const pick = (field: unknown) => {
      let value = field;
      if (typeof value === 'string') {
        try { value = JSON.parse(value); } catch { value = null; }
      }
      if (!value || typeof value !== 'object') return { users_count: 0 };
      const n = Number((value as Record<string, unknown>)['users_count']);
      return { users_count: Number.isFinite(n) ? n : 0 };
    };
    return {
      Road: pick(sub.road),
      Construction: pick(sub.construction),
      Utilities: pick(sub.utilities),
      Consultancy: pick(sub.consultancy),
    };
  }

  recalculate(): void {
    this.calculating = true;
    const domains = this.domainPayload();
    this.apiService.calculateSubscriptionFee({
      duration: this.duration,
      domains,
      Road: domains['Road'],
      Construction: domains['Construction'],
      Utilities: domains['Utilities'],
      Consultancy: domains['Consultancy'],
    }).subscribe({
      next: (res: any) => {
        this.calculating = false;
        const data = res?.data || res;
        this.subtotal = Number(data?.subtotal) || 0;
        this.gstAmount = Number(data?.gstAmount) || 0;
        this.orderTotal = Number(data?.total) || this.subtotal + this.gstAmount;
      },
      error: () => {
        this.calculating = false;
        this.toastrService.error('Failed to calculate renewal amount');
      }
    });
  }

  proceedToPayment(): void {
    const sub = this.data.subscription;
    if (!sub?.id) {
      this.toastrService.error('Subscription not found');
      return;
    }
    if (this.orderTotal <= 0) {
      this.toastrService.warning('Nothing to charge for this renewal');
      return;
    }

    this.loading = true;
    const domains = this.domainPayload();
    this.apiService.createSubscriptionRazorpayOrder({
      amount: this.orderTotal,
      currency: 'INR',
      purpose: 'renew',
      subscription_id: sub.id,
      subscription_data: { duration: this.duration, ...domains },
    }).subscribe({
      next: (response: any) => {
        if (!response.success || !response.data) {
          this.loading = false;
          this.toastrService.error('Failed to create payment order');
          return;
        }
        const options = {
          key: response.data.key,
          amount: response.data.amount,
          currency: response.data.currency,
          name: 'SigmaTConflux Subscription',
          description: 'Subscription renewal',
          order_id: response.data.orderId,
          handler: (paymentResponse: any) => this.applyRenewal(paymentResponse),
          theme: { color: '#0058e9' },
          modal: {
            ondismiss: () => {
              this.loading = false;
            }
          }
        };
        const RazorpayCtor = (window as any).Razorpay;
        if (!RazorpayCtor) {
          this.loading = false;
          this.toastrService.error('Payment checkout failed to load');
          return;
        }
        new RazorpayCtor(options).open();
      },
      error: (err: any) => {
        this.loading = false;
        this.toastrService.error(err.error?.error || 'Failed to start payment');
      }
    });
  }

  private applyRenewal(paymentResponse: any): void {
    const sub = this.data.subscription;
    const domains = this.domainPayload();
    this.apiService.renewSubscription(sub.id, {
      duration: this.duration,
      payment_gateway: 'razorpay',
      payment_id: paymentResponse?.razorpay_payment_id,
      razorpay_order_id: paymentResponse?.razorpay_order_id,
      razorpay_payment_id: paymentResponse?.razorpay_payment_id,
      razorpay_signature: paymentResponse?.razorpay_signature,
      ...domains,
    }).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res.success) {
          this.toastrService.success('Subscription renewed successfully');
          const snapshot = res.data || res;
          this.subscriptionAccessService.applySubscriptionSnapshot(snapshot);
          this.subscriptionAccessService.refreshSubscriptionAccess();
          this.dialogRef.close({ success: true, subscription: snapshot });
        } else {
          this.toastrService.error(res.error || res.message || 'Renewal failed');
        }
      },
      error: (err) => {
        this.loading = false;
        this.toastrService.error(err.error?.error || err.error?.message || 'Renewal failed');
      }
    });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  close(): void {
    this.dialogRef.close();
  }
}
